import { getGroupBySlugPath, getPoolPath, getPoolsPath } from "utils/backend-path-builders";
import { getRequest } from "utils/httpClient";
import { POOL_STATUS_PARAM } from "utils/constants";
import { Pool } from "interfaces/pool";
import { EVENT_ID_QUERY_PARAM } from "home/home-page-query-parameters";
import { UserGroupSettings } from "state/slices/user-settings-slice";

const GROUP_CHANGE_REQUEST = `GROUP_CHANGE_REQUEST`;
const GROUP_CHANGE_RESPONSE = "GROUP_CHANGE_RESPONSE";

export interface IframeMessage {
  type: string;
  payload: object;
}

const postMessageToParentWindow = (message: IframeMessage) => {
  window.parent.postMessage(message, "*");
};

export const postBalanceMessage = (balance: number) => {
  postMessageToParentWindow({
    type: "balance",
    payload: {
      amount: balance
    }
  });
};

export const postAttemptToPlaceABetMessage = (poolId: string) => {
  postMessageToParentWindow({
    type: "user-action",
    payload: {
      actionType: "attempt-to-place-a-bet",
      poolId: poolId
    }
  });
};

// --- Speed Wallet Mini App bridge ---------------------------------------
// Protocol reference:
//   https://speed-wallet.gitbook.io/speed-wallet-mini-apps/receiving-payments.md
//
// Speed Wallet does NOT consume arbitrary {type, payload} shapes. When the
// Mini App wants the host to open its native "Pay invoice" sheet, it must
// post a *stringified* JSON payload in the exact shape Speed documents:
//
//   {
//     "version": "2022-10-15",
//     "account_id": "acct_xxx",
//     "data": {
//       "amount": 25,
//       "currency": "USD",
//       "target_currency": "SATS",
//       "deposit_address": "lnbc1...",
//       "note": "Sparket deposit"
//     }
//   }
//
// The host can be the Android WebView bridge, the iOS WKWebView
// `iosInterface` message handler, or a plain `window.parent` (browser / dev).

/** Speed Mini App protocol version — do not change unless Speed updates theirs. */
const SPEED_PROTOCOL_VERSION = "2022-10-15";

export type SpeedBaseCurrency = "USD" | "EUR" | "BTC" | "SATS";
export type SpeedTargetCurrency = "SATS" | "USDT";

export interface SpeedPaymentRequest {
  /** Speed account id of the wallet user (from the `acct` URL param). */
  accountId: string;
  /** Amount to prompt the user for, in `currency`. */
  amount: number;
  /** Base currency Speed will display the amount in. Defaults to "USD". */
  currency?: SpeedBaseCurrency;
  /** Cryptocurrency the payment settles in. Defaults to "SATS" for LN. */
  targetCurrency?: SpeedTargetCurrency;
  /** BOLT11 invoice, LN address, LNURL, or on-chain address. */
  depositAddress: string;
  /** Optional human-readable note shown in the wallet UI. */
  note?: string;
}

/**
 * Post a message to the Speed Wallet host across all three possible
 * transports (Android bridge, iOS bridge, parent window). The payload is
 * always stringified per Speed's protocol docs. Transport order mirrors
 * the snippet from speed-wallet.gitbook.io/speed-wallet-mini-apps/receiving-payments.md
 * exactly — Android → iOS → window.parent → window.
 */
const postToSpeedHost = (payload: unknown) => {
  if (typeof window === "undefined") {
    return;
  }

  const data = JSON.stringify(payload);

  const w = window as unknown as {
    Android?: { postMessage?: (data: string) => void };
    webkit?: {
      messageHandlers?: {
        iosInterface?: { postMessage?: (data: string) => void };
      };
    };
  };

  if (w.Android && typeof w.Android.postMessage === "function") {
    // eslint-disable-next-line no-console
    console.log("[speed] postMessage via Android bridge", data);
    w.Android.postMessage(data);
    return;
  }

  const iosInterface = w.webkit?.messageHandlers?.iosInterface;
  if (iosInterface && typeof iosInterface.postMessage === "function") {
    // eslint-disable-next-line no-console
    console.log("[speed] postMessage via iOS webkit bridge", data);
    iosInterface.postMessage(data);
    return;
  }

  if (window.parent) {
    // eslint-disable-next-line no-console
    console.log("[speed] postMessage via window.parent", data);
    window.parent.postMessage(data, "*");
    return;
  }

  // eslint-disable-next-line no-console
  console.log("[speed] postMessage via window (no parent)", data);
  window.postMessage(data, "*");
};

/**
 * Ask the Speed Wallet host to open its native "Pay invoice" flow for the
 * given BOLT11 invoice (or LN address / LNURL / on-chain address). Used
 * during deposit: Sparket creates an invoice on its Speed merchant account
 * and hands it to the wallet so the user can confirm with a single tap.
 *
 * The payload shape is mandated by the Speed Mini Apps protocol; anything
 * else is rejected by the host with "invalid data provided".
 */
export const postSpeedPayInvoiceMessage = (req: SpeedPaymentRequest) => {
  const payload = {
    version: SPEED_PROTOCOL_VERSION,
    account_id: req.accountId,
    data: {
      amount: req.amount,
      currency: req.currency ?? "USD",
      target_currency: req.targetCurrency ?? "SATS",
      deposit_address: req.depositAddress,
      ...(req.note ? { note: req.note } : {}),
    },
  };

  // eslint-disable-next-line no-console
  console.log("[speed] payInvoice payload", payload);
  postToSpeedHost(payload);
};

export let groupChangeRequestListener: any | undefined;

const extractPoolId = (pathname: string): string | null => {
  const match = pathname.match(/\/pools\/([0-9a-fA-F-]{36})/);
  return match ? match[1] : null;
};

const handleCurrencyChangeRequest = async (event: MessageEvent, userGroupSettings: UserGroupSettings | undefined) => {
  let correspondingPage = "/";

  const poolId = extractPoolId(window.location.pathname);

  if (poolId) {
    const { groupSlug } = event.data.payload;

    const groupPath = getGroupBySlugPath(groupSlug);
    const groupResponse = await getRequest(groupPath, { skipIntegrationApi: true });
    if (!groupResponse) {
      console.error(`unable to process CHANGE_GROUP_REQUEST - no group found by ${groupSlug}`);
      return;
    }

    const changeToGroupId = groupResponse.id;

    const pools = await getRequest(getPoolsPath(changeToGroupId), {
      queryParams: {
        status: POOL_STATUS_PARAM.OPEN,
        pg: 1,
        page_size: 10000,
        sortBy: "weight:desc,close:asc"
      },
      skipIntegrationApi: true
    });

    const currentPool = await getRequest(
      getPoolPath(changeToGroupId, poolId),
      { skipIntegrationApi: true }
    );

    const correspondingPool = pools.find(
      (p: Pool) =>
        p.name === currentPool.name &&
        p.start === currentPool.start &&
        p.close === currentPool.close &&
        p.event_id === currentPool.event_id
    );

    if (correspondingPool) {
      correspondingPage = `/pools/${correspondingPool.id}`;
    }
  } else if (window.location.pathname === "/") {
    const homePagePoolsEventIdFilter = userGroupSettings?.homePagePoolsEventIdFilter;
    if (homePagePoolsEventIdFilter) {
      correspondingPage = `/?${EVENT_ID_QUERY_PARAM}=${homePagePoolsEventIdFilter}`;
    } else {
      correspondingPage = `/?${EVENT_ID_QUERY_PARAM}=`;
    }
  } else {
    correspondingPage = window.location.pathname;
  }

  const responseMessage = {
    type: GROUP_CHANGE_RESPONSE,
    payload: {
      correspondingPage: correspondingPage
    }
  };

  window.parent.postMessage(responseMessage, event.origin);
};

export const addGroupChangeRequestMessageListener = (userGroupSettings: React.MutableRefObject<{
  userGroupSettings: UserGroupSettings | undefined
}>) => {
  if (!groupChangeRequestListener) {
    groupChangeRequestListener = (event: MessageEvent) => {
      const { type } = event.data;

      if (type === GROUP_CHANGE_REQUEST) {
        handleCurrencyChangeRequest(event, userGroupSettings.current.userGroupSettings)
          .catch(e => console.error(`unable to process ${GROUP_CHANGE_REQUEST}`, e));
      }
    };

    window.addEventListener("message", groupChangeRequestListener);
  }
};